import React from 'react';
import { PanelLeft, Search } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import { AuthenticatedTemplate } from '../AuthenticatedTemplate';
import { PendingApprovalsHeaderIcon } from '../header/PendingApprovalsHeaderIcon';
import { ThemeSelector } from '../ThemeSelector';
import { EndOnBehalfOfButton } from '../ImpersonationBanner';
import { UserInfo } from '../UserInfo';
import { SignOutButton } from '../SignOutButton';
import type { RequestApprovalTypeSummaryItem } from '../../types/EntityEntitlements';

type NavigationCategory = 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin';

interface HeaderProps {
    isDashboard: boolean;
    navRailOpen: boolean;
    onToggleNavRail: () => void;
    pageLabel: string;
    categoryLabel: string | null;
    requestApprovalSummary: RequestApprovalTypeSummaryItem[];
    onNavigate: (destination: string, isEntity?: boolean, category?: NavigationCategory) => void;
    onImpersonationChange: () => void;
    onOpenCommandPalette: () => void;
}

export const Header: React.FC<HeaderProps> = ({
    isDashboard,
    navRailOpen,
    onToggleNavRail,
    pageLabel,
    categoryLabel,
    requestApprovalSummary,
    onNavigate,
    onImpersonationChange,
    onOpenCommandPalette,
}) => {
    return (
        <TooltipProvider delayDuration={300}>
            <header
                className="sticky top-0 z-[200] h-14 flex items-center px-4 gap-3 shrink-0"
                style={{
                    background: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                }}
            >
                {/* Left: nav toggle + branding */}
                <div className="flex items-center gap-3 shrink-0">
                    {!isDashboard && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={onToggleNavRail}
                                    className="flex items-center justify-center w-8 h-8 rounded-md transition-colors hover:bg-white/10"
                                    aria-label={navRailOpen ? 'Hide navigation' : 'Show navigation'}
                                >
                                    <PanelLeft size={20} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>{navRailOpen ? 'Hide navigation' : 'Show navigation'}</TooltipContent>
                        </Tooltip>
                    )}
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-lg font-bold leading-none">Hades</span>
                        <span className="text-[11px] font-medium leading-none opacity-55">Powered by SwinTek</span>
                    </div>
                </div>

                {/* Center: page title */}
                <div className="flex-1" />
                <span
                    className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none font-semibold text-lg opacity-90"
                >
                    {categoryLabel && (
                        <span className="hidden md:inline">{categoryLabel} - </span>
                    )}
                    {pageLabel}
                </span>
                <div className="flex-1" />

                {/* Right: actions */}
                <AuthenticatedTemplate>
                    <div className="flex items-center gap-1">
                        {/* Command palette trigger */}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={onOpenCommandPalette}
                                    className="flex items-center justify-center w-8 h-8 rounded-md transition-colors hover:bg-white/10"
                                    aria-label="Search (Ctrl+K)"
                                >
                                    <Search size={18} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>Search <kbd className="ml-1 text-[10px] px-1 py-0.5 rounded bg-white/20">Ctrl+K</kbd></TooltipContent>
                        </Tooltip>

                        <PendingApprovalsHeaderIcon
                            summary={requestApprovalSummary}
                            onNavigate={onNavigate}
                        />
                        <ThemeSelector variant="compact" />
                        <EndOnBehalfOfButton onSessionEnd={onImpersonationChange} />
                    </div>
                    <div className="ml-2">
                        <UserInfo />
                    </div>
                    <div className="ml-2">
                        <SignOutButton />
                    </div>
                </AuthenticatedTemplate>
            </header>
        </TooltipProvider>
    );
};
